/* global Excel */

/**
 * Tier 1 catalog management — writes/reads 4 in-workbook sheets:
 *   - <catalog>_BaseWorks
 *   - <catalog>_Labour
 *   - <catalog>_Equipment
 *   - <catalog>_Material
 *   - <catalog>_PriceList
 *
 * The Labour/Equipment/Material sheets are matrices: rows are resources,
 * columns 1..N are fixed metadata, columns N+1..M are one-per-base-work-code
 * with the quantity per base unit.
 */

import { formatCodeColumnsAsText } from "./sheetUtils";

const TYPE_LABEL ={ LABOUR: "Labour", EQUIPMENT: "Equipment", MATERIAL: "Material" };

const HEADER_FILL = "#0F766E";
const HEADER_FILL_LIGHT = "#14b8a6";
const HEADER_FONT = "#FFFFFF";

function safe(name) { return String(name || "Catalog").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 24); }

export const CATALOG_TABLE_HEADERS = [
  "Work_Group_Code",
  "Work_Group_Description_GEO",
  "Work_Group_Description_ENG",
  "Work_Group_Description_RUS",
  "Level_1_Parameter_GEO",
  "Level_1_Parameter_ENG",
  "Level_1_Parameter_RUS",
  "Level_2_Parameter_GEO",
  "Level_2_Parameter_ENG",
  "Level_2_Parameter_RUS",
  "Level_3_Parameter_GEO",
  "Level_3_Parameter_ENG",
  "Level_3_Parameter_RUS",
  "Level_1_Value_GEO",
  "Level_1_Value_ENG",
  "Level_1_Value_RUS",
  "Level_2_Value_GEO",
  "Level_2_Value_ENG",
  "Level_2_Value_RUS",
  "Level_3_Value_GEO",
  "Level_3_Value_ENG",
  "Level_3_Value_RUS",
  "Base_Work_Code",
  "Work_Name_GEO",
  "Work_Name_ENG",
  "Work_Name_RUS",
  "Base_Unit",
  "Base_Unit_Value",
  "Secondary_Unit",
  "Secondary_Unit_Factor",
  "Unit_Rate",
  "Unit_Rate_Min",
  "Unit_Rate_Max",
  "Currency",
  "GESN_Code",
  "Costy_Code",
  "SNIP_Code",
  "UniClass_Code",
  "OmniClass_Code",
  "MasterFormat_Code",
  "Notes",
];

export function catalogTableSheetName(catalogName) {
  return `${safe(catalogName)}_Table`;
}

export function requirementsSheetName(catalogName) {
  return `${safe(catalogName)}_Reqs`;
}

export const REQUIREMENTS_HEADERS = [
  "ID",
  "Action",                  // blank or "delete"
  "Work_Group_Code",         // read-only context
  "Base_Work_Code",          // required
  "Base_Work_Name",          // read-only context
  "Base_Work_Base_Unit",     // read-only context
  "Kind",                    // MATERIAL / EQUIPMENT / LABOUR
  "Classification_Code",     // either this …
  "Product_Display_ID",      // … or this
  "Reference_Name",          // read-only context (resolved name)
  "Quantity_per_Base_Unit",
  "Unit_Code",
  "Note",
];

/**
 * Writes the master requirements sheet for an entire catalog. Each row is
 * one base_work_requirement. User can edit, add new rows, set Action="delete"
 * to remove rows, then save back.
 */
export async function writeRequirementsToExcel(catalogName, requirements = []) {
  const sheetName = requirementsSheetName(catalogName);
  await Excel.run(async (context) => {
    const wb = context.workbook;
    let sheet = wb.worksheets.getItemOrNullObject(sheetName);
    sheet.load("name");
    await context.sync();
    if (sheet.isNullObject) {
      sheet = wb.worksheets.add(sheetName);
      await context.sync();
    } else {
      const used = sheet.getUsedRangeOrNullObject();
      used.load("isNullObject");
      await context.sync();
      if (!used.isNullObject) {
        used.clear();
        await context.sync();
      }
    }

    const rows = (requirements || []).map((r) => ({
      ID: r.id,
      Action: "",
      Work_Group_Code: r.work_group_code,
      Base_Work_Code: r.base_work_code,
      Base_Work_Name: r.base_work_name_geo || r.base_work_name_eng || "",
      Base_Work_Base_Unit: r.base_work_base_unit_value
        ? `${r.base_work_base_unit_value} ${r.base_work_base_unit || ""}`.trim()
        : (r.base_work_base_unit || ""),
      Kind: r.requirement_kind,
      Classification_Code: r.classification_code || "",
      Product_Display_ID: r.product_display_id || "",
      Reference_Name: r.product_id
        ? (r.product_name_geo || r.product_name_eng || "")
        : (r.classification_name_geo || r.classification_name_eng || ""),
      Quantity_per_Base_Unit: r.quantity_per_base_unit,
      Unit_Code: r.unit_code || r.product_base_unit || "",
      Note: r.note || "",
    }));

    const headers = REQUIREMENTS_HEADERS;
    const totalRows = 1 + rows.length;
    const totalCols = headers.length;
    const data = [headers, ...rows.map((r) => headers.map((h) => {
      const v = r[h];
      return v === undefined || v === null ? "" : v;
    }))];

    formatCodeColumnsAsText(sheet, headers, totalRows);
    sheet.getRangeByIndexes(0, 0, totalRows, totalCols).values = data;

    // Header style
    const headerRange = sheet.getRangeByIndexes(0, 0, 1, totalCols);
    headerRange.format.fill.color = "#0F766E";
    headerRange.format.font.color = "#FFFFFF";
    headerRange.format.font.bold = true;
    headerRange.format.horizontalAlignment = "Center";

    // Read-only context columns get a subtle grey tint so users know which to edit
    const readOnlyCols = [2, 4, 5, 9]; // Work_Group_Code, Base_Work_Name, Base_Work_Base_Unit, Reference_Name
    readOnlyCols.forEach((col) => {
      if (totalRows > 1) {
        sheet.getRangeByIndexes(1, col, totalRows - 1, 1).format.fill.color = "#F3F4F6";
      }
    });

    sheet.freezePanes.freezeRows(1);
    sheet.getUsedRange().format.autofitColumns();
    sheet.activate();
    await context.sync();
  });
  return sheetName;
}

export async function readRequirementsFromExcel(catalogName) {
  const sheetName = requirementsSheetName(catalogName);
  let result = [];
  await Excel.run(async (context) => {
    const wb = context.workbook;
    const sheet = wb.worksheets.getItemOrNullObject(sheetName);
    sheet.load("name");
    await context.sync();
    if (sheet.isNullObject) {
      throw new Error(`Sheet "${sheetName}" not found. Click "Load requirements to Excel" first.`);
    }
    const used = sheet.getUsedRangeOrNullObject();
    used.load(["values", "isNullObject"]);
    await context.sync();
    if (used.isNullObject || !used.values || used.values.length < 2) return;
    const [headerRow, ...bodyRows] = used.values;
    const headers = headerRow.map((h) => (h == null ? "" : String(h).trim()));
    result = bodyRows
      .map((r) => {
        const obj = {};
        let nonEmpty = false;
        headers.forEach((h, i) => {
          const v = r[i];
          if (v !== null && v !== undefined && v !== "") nonEmpty = true;
          obj[h] = v;
        });
        return nonEmpty ? obj : null;
      })
      .filter(Boolean);
  });
  return result;
}

const BASE_WORK_HEADERS = [
  "Work_Group_Code",
  "Work_Group_Name_GEO",
  "Work_Group_Name_ENG",
  "Work_Group_Name_RUS",
  "Base_Work_Code",
  "Name_GEO",
  "Name_ENG",
  "Name_RUS",
  "Base_Unit",
  "Secondary_Unit",
  "Secondary_Unit_Factor",
  "Unit_Rate",
  "Currency",
  "Notes",
];

const RESOURCE_LONG_HEADERS = [
  "Base_Work_Code",
  "Resource_Code",
  "Generic",
  "Name_GEO",
  "Name_ENG",
  "Name_RUS",
  "Base_Unit",
  "Quantity",
];

const PRICE_HEADERS = [
  "Resource_Code",
  "Resource_Name",
  "Extra_Code",
  "Description_GEO",
  "Description_ENG",
  "Description_RUS",
  "Unit_Rate",
  "Currency",
  "Price_Date",
  "Note",
];

async function ensureSheet(context, name) {
  const wb = context.workbook;
  let sheet = wb.worksheets.getItemOrNullObject(name);
  sheet.load("name");
  await context.sync();
  if (sheet.isNullObject) {
    sheet = wb.worksheets.add(name);
    await context.sync();
  } else {
    const used = sheet.getUsedRangeOrNullObject();
    used.load("isNullObject");
    await context.sync();
    if (!used.isNullObject) {
      used.clear();
      await context.sync();
    }
  }
  return sheet;
}

function styleHeader(sheet, rows, cols, fill = HEADER_FILL) {
  const header = sheet.getRangeByIndexes(0, 0, 1, cols);
  header.format.fill.color = fill;
  header.format.font.color = HEADER_FONT;
  header.format.font.bold = true;
  header.format.horizontalAlignment = "Center";
  if (rows > 1) {
    sheet.getRangeByIndexes(0, 0, rows, cols).format.borders.getItem("InsideHorizontal").style = "Continuous";
    sheet.getRangeByIndexes(0, 0, rows, cols).format.borders.getItem("InsideVertical").style = "Continuous";
    sheet.getRangeByIndexes(0, 0, rows, cols).format.borders.getItem("EdgeTop").style = "Continuous";
    sheet.getRangeByIndexes(0, 0, rows, cols).format.borders.getItem("EdgeBottom").style = "Continuous";
    sheet.getRangeByIndexes(0, 0, rows, cols).format.borders.getItem("EdgeLeft").style = "Continuous";
    sheet.getRangeByIndexes(0, 0, rows, cols).format.borders.getItem("EdgeRight").style = "Continuous";
  }
}

function fillSheet(sheet, headers, rows) {
  const totalRows = 1 + rows.length;
  const totalCols = headers.length;
  const values = [headers, ...rows.map((r) => headers.map((_, i) => (r[i] === undefined ? null : r[i])))];
  if (totalRows > 0 && totalCols > 0) {
    formatCodeColumnsAsText(sheet, headers, totalRows);
    sheet.getRangeByIndexes(0, 0, totalRows, totalCols).values = values;
  }
}

/**
 * Writes the 4-tab bundle returned by GET /tier1/catalogs/{id}/sheets.
 *
 * @param {Object} bundle — the JSON bundle from the API
 * @returns {Promise<{baseWorks: string, labour: string, equipment: string, material: string, prices: string}>}
 */
export async function writeTier1Sheets(bundle) {
  const cat = safe(bundle.catalog_name || `Cat${bundle.catalog_id}`);
  const sheetNames = {
    baseWorks: `${cat}_BaseWorks`,
    labour: `${cat}_Labour`,
    equipment: `${cat}_Equipment`,
    material: `${cat}_Material`,
    prices: `${cat}_PriceList`,
  };

  await Excel.run(async (context) => {
    // 1. BaseWorks sheet
    const baseSheet = await ensureSheet(context, sheetNames.baseWorks);
    const groupByCode = {};
    (bundle.work_groups || []).forEach((g) => { groupByCode[g.code] = g; });
    const bwRows = (bundle.base_works || []).map((bw) => {
      const g = groupByCode[bw.work_group_code] || {};
      return [
        bw.work_group_code,
        g.name_geo || "",
        g.name_eng || "",
        g.name_rus || "",
        bw.code,
        bw.name_geo || "",
        bw.name_eng || "",
        bw.name_rus || "",
        bw.base_unit || "",
        bw.secondary_unit || "",
        bw.secondary_unit_factor ?? "",
        bw.unit_rate ?? "",
        bw.currency || "",
        bw.notes || "",
      ];
    });
    fillSheet(baseSheet, BASE_WORK_HEADERS, bwRows);
    styleHeader(baseSheet, 1 + bwRows.length, BASE_WORK_HEADERS.length);
    baseSheet.getUsedRange().format.autofitColumns();

    // 2. Three long-format sheets — one row per (Base_Work_Code, Resource).
    // Empty rows are still written for selected base codes with no resources
    // yet, so the user can fill them in.
    const baseCodes = (bundle.base_works || []).map((b) => b.code);
    for (const [bucket, list] of [
      ["LABOUR", bundle.labour || []],
      ["EQUIPMENT", bundle.equipment || []],
      ["MATERIAL", bundle.material || []],
    ]) {
      const sheetName = sheetNames[bucket.toLowerCase()];
      const sheet = await ensureSheet(context, sheetName);
      const headers = RESOURCE_LONG_HEADERS;

      // Build rows ordered first by base_work_code, then by resource_code.
      // For each base code, emit one row per resource that has a quantity > 0
      // (or any non-null quantity) for that code.
      const rows = [];
      for (const bwCode of baseCodes) {
        const matches = list
          .map((cell) => {
            const q = cell.quantities?.[bwCode];
            if (q === undefined || q === null || q === "") return null;
            return { cell, qty: q };
          })
          .filter(Boolean)
          .sort((a, b) => (a.cell.code || "").localeCompare(b.cell.code || ""));
        for (const { cell, qty } of matches) {
          rows.push([
            bwCode,
            cell.code,
            cell.is_generic ? "YES" : "",
            cell.name_geo || "",
            cell.name_eng || "",
            cell.name_rus || "",
            cell.base_unit || "",
            qty,
          ]);
        }
      }

      fillSheet(sheet, headers, rows);
      styleHeader(sheet, 1 + rows.length, headers.length, HEADER_FILL_LIGHT);
      sheet.freezePanes.freezeRows(1);
      sheet.getUsedRange().format.autofitColumns();
    }

    // 3. Price list sheet
    const pricesSheet = await ensureSheet(context, sheetNames.prices);
    const priceRows = (bundle.prices || []).map((p) => [
      p.resource_code,
      p.resource_name || "",
      p.extra_code || "",
      p.description_geo || "",
      p.description_eng || "",
      p.description_rus || "",
      p.unit_rate ?? "",
      p.currency || bundle.price_list_currency || "",
      p.price_date || "",
      p.note || "",
    ]);
    fillSheet(pricesSheet, PRICE_HEADERS, priceRows);
    styleHeader(pricesSheet, 1 + priceRows.length, PRICE_HEADERS.length);
    if (priceRows.length) {
      pricesSheet.getRangeByIndexes(1, 8, priceRows.length, 1).numberFormat = [["yyyy-mm-dd"]];
    }
    pricesSheet.getUsedRange().format.autofitColumns();

    baseSheet.activate();
    await context.sync();
  });

  return sheetNames;
}

/**
 * Writes the catalog main table to a single sheet. If `rows` is empty, only
 * the header row is written so the user can fill it in.
 *
 * @param {string} catalogName
 * @param {Array<Object>} rows — keyed by `headers`
 * @param {Array<string>} [headers] — defaults to the full Tier 1 header set;
 *        callers should pass the tier-specific header list returned by the API.
 * @returns {Promise<string>} the sheet name created
 */
export async function writeCatalogMainTable(catalogName, rows = [], headers = null) {
  const sheetName = catalogTableSheetName(catalogName);
  await Excel.run(async (context) => {
    const sheet = await ensureSheet(context, sheetName);
    const headerSet = (Array.isArray(headers) && headers.length) ? headers : CATALOG_TABLE_HEADERS;
    const totalRows = 1 + rows.length;
    const totalCols = headerSet.length;

    const data = [headerSet, ...rows.map((r) => headerSet.map((h) => {
      const v = r[h];
      return v === undefined || v === null ? "" : v;
    }))];
    formatCodeColumnsAsText(sheet, headerSet, totalRows);
    sheet.getRangeByIndexes(0, 0, totalRows, totalCols).values = data;

    styleHeader(sheet, totalRows, totalCols, HEADER_FILL);
    sheet.freezePanes.freezeRows(1);
    sheet.getUsedRange().format.autofitColumns();
    sheet.activate();
    await context.sync();
  });
  return sheetName;
}

/**
 * Reads the catalog main table sheet back as an array of objects keyed by
 * CATALOG_TABLE_HEADERS. Skips empty rows.
 *
 * @param {string} catalogName
 * @returns {Promise<Array<Object>>}
 */
export async function readCatalogMainTable(catalogName) {
  const sheetName = catalogTableSheetName(catalogName);
  let result = [];
  await Excel.run(async (context) => {
    const wb = context.workbook;
    const sheet = wb.worksheets.getItemOrNullObject(sheetName);
    sheet.load("name");
    await context.sync();
    if (sheet.isNullObject) {
      throw new Error(`Sheet "${sheetName}" not found. Click "Load catalog table" first.`);
    }
    const used = sheet.getUsedRangeOrNullObject();
    used.load(["values", "isNullObject"]);
    await context.sync();
    if (used.isNullObject || !used.values || used.values.length < 2) return;
    const [headerRow, ...bodyRows] = used.values;
    const headers = headerRow.map((h) => (h == null ? "" : String(h).trim()));
    result = bodyRows
      .map((r) => {
        const obj = {};
        let nonEmpty = false;
        headers.forEach((h, i) => {
          const v = r[i];
          if (v !== null && v !== undefined && v !== "") nonEmpty = true;
          obj[h] = v;
        });
        return nonEmpty ? obj : null;
      })
      .filter(Boolean);
  });
  return result;
}

function readSheetAsObjects(values) {
  if (!values || values.length < 2) return { headers: [], rows: [] };
  const [headerRow, ...bodyRows] = values;
  const headers = headerRow.map((h) => (h == null ? "" : String(h).trim()));
  const rows = bodyRows
    .map((r) => {
      const obj = {};
      let nonEmpty = false;
      headers.forEach((h, i) => {
        const v = r[i];
        if (v !== null && v !== undefined && v !== "") nonEmpty = true;
        obj[h] = v;
      });
      return nonEmpty ? obj : null;
    })
    .filter(Boolean);
  return { headers, rows };
}

/**
 * Reads the 4-tab bundle back into the bundle shape that the POST endpoint expects.
 *
 * @param {string} catalogName — used to derive sheet names
 * @param {number} catalogId
 * @param {{id: number, name: string, currency: string}} priceListMeta
 */
export async function readTier1Sheets(catalogName, catalogId, priceListMeta = {}) {
  const cat = safe(catalogName);
  const sheetNames = {
    baseWorks: `${cat}_BaseWorks`,
    labour: `${cat}_Labour`,
    equipment: `${cat}_Equipment`,
    material: `${cat}_Material`,
    prices: `${cat}_PriceList`,
  };

  const result = {
    catalog_id: catalogId,
    base_works: [],
    work_groups: [],
    labour: [],
    equipment: [],
    material: [],
    price_list_id: priceListMeta?.id ?? null,
    price_list_name: priceListMeta?.name ?? null,
    price_list_currency: priceListMeta?.currency ?? null,
    prices: [],
  };

  await Excel.run(async (context) => {
    const wb = context.workbook;
    const proxies = {};
    const usedProxies = {};
    for (const key of Object.keys(sheetNames)) {
      const s = wb.worksheets.getItemOrNullObject(sheetNames[key]);
      s.load("name");
      proxies[key] = s;
    }
    await context.sync();

    for (const key of Object.keys(proxies)) {
      const s = proxies[key];
      if (s.isNullObject) continue;
      const used = s.getUsedRangeOrNullObject();
      used.load(["values", "isNullObject"]);
      usedProxies[key] = used;
    }
    await context.sync();

    // BaseWorks
    if (usedProxies.baseWorks && !usedProxies.baseWorks.isNullObject) {
      const { rows } = readSheetAsObjects(usedProxies.baseWorks.values);
      const wgSeen = {};
      for (const r of rows) {
        const wgCode = String(r.Work_Group_Code || "").trim();
        if (!wgCode) continue;
        if (!wgSeen[wgCode]) {
          wgSeen[wgCode] = {
            code: wgCode,
            name_geo: r.Work_Group_Name_GEO || null,
            name_eng: r.Work_Group_Name_ENG || null,
            name_rus: r.Work_Group_Name_RUS || null,
          };
          result.work_groups.push(wgSeen[wgCode]);
        }
        if (!r.Base_Work_Code) continue;
        result.base_works.push({
          work_group_code: wgCode,
          code: String(r.Base_Work_Code).trim(),
          name_geo: r.Name_GEO || null,
          name_eng: r.Name_ENG || null,
          name_rus: r.Name_RUS || null,
          base_unit: r.Base_Unit || null,
          secondary_unit: r.Secondary_Unit || null,
          secondary_unit_factor: r.Secondary_Unit_Factor === "" ? null : r.Secondary_Unit_Factor,
          unit_rate: r.Unit_Rate === "" ? null : r.Unit_Rate,
          currency: r.Currency || null,
          notes: r.Notes || null,
        });
      }
    }

    // Three long-format sheets — pivot back into one cell per resource code
    // with a quantities map keyed by Base_Work_Code.
    for (const [bucketKey, bucket] of [
      ["labour", "LABOUR"],
      ["equipment", "EQUIPMENT"],
      ["material", "MATERIAL"],
    ]) {
      const used = usedProxies[bucketKey];
      if (!used || used.isNullObject) continue;
      const { rows: longRows } = readSheetAsObjects(used.values);
      if (!longRows.length) continue;

      const cellByCode = {};
      for (const r of longRows) {
        const bwCode = r.Base_Work_Code ? String(r.Base_Work_Code).trim() : "";
        const resCode = r.Resource_Code ? String(r.Resource_Code).trim() : "";
        if (!bwCode || !resCode) continue;
        const qty = r.Quantity;
        if (qty === null || qty === undefined || qty === "") continue;

        let cell = cellByCode[resCode];
        if (!cell) {
          cell = {
            resource_code: resCode,
            resource_type: bucket,
            is_generic: String(r.Generic || "").toUpperCase() === "YES",
            name_geo: r.Name_GEO || null,
            name_eng: r.Name_ENG || null,
            name_rus: r.Name_RUS || null,
            base_unit: r.Base_Unit || null,
            quantities: {},
          };
          cellByCode[resCode] = cell;
        } else {
          // Take metadata from the first row, but accept overrides from later
          // rows when the earlier ones were empty.
          if (!cell.name_geo && r.Name_GEO) cell.name_geo = r.Name_GEO;
          if (!cell.name_eng && r.Name_ENG) cell.name_eng = r.Name_ENG;
          if (!cell.name_rus && r.Name_RUS) cell.name_rus = r.Name_RUS;
          if (!cell.base_unit && r.Base_Unit) cell.base_unit = r.Base_Unit;
        }
        cell.quantities[bwCode] = qty;
      }
      result[bucketKey] = Object.values(cellByCode);
    }

    // Prices
    if (usedProxies.prices && !usedProxies.prices.isNullObject) {
      const { rows } = readSheetAsObjects(usedProxies.prices.values);
      for (const r of rows) {
        if (!r.Resource_Code) continue;
        let priceDate = r.Price_Date || null;
        if (priceDate instanceof Date) {
          priceDate = priceDate.toISOString().slice(0, 10);
        } else if (typeof priceDate === "number") {
          // Excel date serial → ISO date
          const d = new Date(Math.round((priceDate - 25569) * 86400 * 1000));
          priceDate = isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
        }
        result.prices.push({
          resource_code: String(r.Resource_Code).trim(),
          resource_name: r.Resource_Name || null,
          extra_code: r.Extra_Code ? String(r.Extra_Code).trim() : null,
          description_geo: r.Description_GEO || null,
          description_eng: r.Description_ENG || null,
          description_rus: r.Description_RUS || null,
          unit_rate: r.Unit_Rate === "" ? 0 : Number(r.Unit_Rate) || 0,
          currency: r.Currency || null,
          price_date: priceDate,
          note: r.Note || null,
        });
      }
    }
  });

  return result;
}
